import { useCompany } from '@/hooks/useCompany'
import { useIntegrationsTabRouter, INTEGRATIONS_TABS } from '@/hooks/useIntegrationsTabRouter'
import { IntegrationsList } from './integrations/IntegrationsList'
import { InboundWebhooksPanel } from './integrations/inboundWebhooks/InboundWebhooksPanel'
import { OutboundWebhooksPanel } from './integrations/outboundWebhooks/OutboundWebhooksPanel'

export function SettingsIntegrations() {
  const { companyId, workspaces } = useCompany()
  const { activeTab, routeToTab } = useIntegrationsTabRouter(companyId || '')

  if (!companyId) {
    return (
      <div className="px-6 py-4 max-w-7xl mx-auto">
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Integrations</h2>
            <p className="text-sm mt-1 text-slate-600 dark:text-slate-400 max-w-prose">Connect external platforms and move leads in and out of OrbitalX.</p>
          </div>
          <div className="mt-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              No company selected.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-6 py-4 max-w-7xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Integrations</h2>
        <p className="text-sm mt-1 text-slate-600 dark:text-slate-400 max-w-prose">
          Connect external platforms and move leads in and out of OrbitalX.
        </p>
      </div>

      {/*
        Tabs rather than stacked sections. Each webhook surface is a full master-detail screen, and
        as sections they sat below the provider grid — which grows with every integration added, so
        outbound kept sinking further out of reach. A tester could not find it at all.

        Mirrors the WebDetail tab treatment step for step, so the app has one tab idiom rather than
        two that look almost the same.
      */}
      <div role="tablist" aria-label="Integrations sections" className="flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-slate-700">
        {INTEGRATIONS_TABS.map(tab => (
          // aria-controls is set only on the selected tab: one panel is in the DOM at a time, and an
          // aria-controls naming an id that does not exist is worse than none — a screen reader
          // follows it and lands nowhere.
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`integrations-tab-${tab.key}`}
            aria-selected={activeTab === tab.key}
            aria-controls={activeTab === tab.key ? `integrations-panel-${tab.key}` : undefined}
            onClick={() => routeToTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
              ${activeTab === tab.key ? 'text-primary-700 dark:text-primary-400' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}
            `}
          >
            {tab.label}
            <span className={`absolute left-0 right-0 -bottom-px h-0.5 rounded-full transition-all ${activeTab === tab.key ? 'bg-primary-600 dark:bg-primary-500' : 'bg-transparent'}`} />
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`integrations-panel-${activeTab}`}
        aria-labelledby={`integrations-tab-${activeTab}`}
      >
        {activeTab === 'connections' && <IntegrationsList clientCompanyId={companyId} />}
        {activeTab === 'inbound' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-prose">
              Issue a key so an external system &mdash; Zapier, a partner&apos;s SDR service, a legacy CRM &mdash;
              can post leads into one workspace.
            </p>
            <InboundWebhooksPanel clientCompanyId={companyId} workspaces={workspaces} />
          </div>
        )}
        {activeTab === 'outbound' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-prose">
              Push every processed lead to an external endpoint, such as a partner&apos;s SDR service.
            </p>
            <OutboundWebhooksPanel clientCompanyId={companyId} workspaces={workspaces} />
          </div>
        )}
      </div>
    </div>
  )
}
